import { FC, useContext, useMemo } from 'react';

import { Label, Table } from '@smartshell/ui-kit';
import { Column, Row, TableState } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { ETypeSlice, TNormalizedItem } from '../types';
import { formatMoney, formatPercents, formatSeconds } from '../utils';
import s from './TableBlock.module.scss';

interface ITableBlockProps {
  data: Array<TNormalizedItem>;
  loading: boolean;
  apiCalled: boolean;
  type: ETypeSlice;
}

export const TableBlock: FC<ITableBlockProps> = ({
  type,
  data,
  apiCalled,
  loading,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const COLUMNS = useMemo<Array<Column<TNormalizedItem>>>(
    () => [
      {
        id: 'alias',
        accessor: 'alias',
        Header: t('gamingPlace'),
        width: 176,
      },
      {
        accessor: 'groupTitle',
        Header: t('report.hosts.group'),
        Cell: ({ value }) => (
          <Label text={value} color="gray-primary" rounded />
        ),
        width: 200,
      },
      {
        id: 'hostCount',
        accessor: 'hostCount',
        Header: t('report.hosts.count'),
        Cell: ({ value }) => `${value} ${t('pcs')}`,
        width: 156,
        disableResizing: true,
      },
      {
        id: 'totalTime',
        accessor: ({ total }) => total.time,
        Cell: ({
          row: {
            original: {
              total: { time, percentTime },
            },
          },
        }: {
          row: Row<TNormalizedItem>;
        }) => (
          <div className={s.totalOccupation}>
            {formatSeconds(t, time)}
            {isNaN(percentTime) ? (
              <Label
                iconName="ExclamationIcon"
                color="orange"
                helperText={t('report.hosts.dataContainErrors')}
              />
            ) : (
              <Label text={formatPercents(percentTime)} rounded />
            )}
          </div>
        ),
        Header: t('report.hosts.total.occupation.short'),
        disableResizing: true,
        width: 177,
      },
      {
        accessor: 'averageSessionTime',
        Header: t('report.hosts.average.session.short'),
        Cell: ({ value }) => (
          <span className={s.averageSession}>{formatSeconds(t, value)}</span>
        ),
        disableResizing: true,
        width: 179,
      },
      {
        accessor: 'sessionsCount',
        Header: t('report.hosts.sessions.count'),
        Cell: ({ value }) => `${value} ${t('pcs')}`,
        disableResizing: true,
        width: 176,
      },
      {
        id: 'averageProceeds',
        accessor: ({ total: { proceeds }, hostCount }) =>
          hostCount ? proceeds / hostCount : null,
        Header: t('report.hosts.average.proceeds.byHost'),
        Cell: ({ value }: { value: number }) => formatMoney(value, currency),
        disableResizing: true,
        width: 233,
      },
      {
        id: 'proceeds',
        accessor: ({ total }) => total.proceeds,
        Cell: ({ value }: { value: number }) => formatMoney(value, currency),
        Header: t('money.proceeds'),
        disableResizing: true,
        width: 154,
      },
    ],
    [currency, t]
  );

  const useControlledState = (
    state: TableState<TNormalizedItem>
  ): TableState<TNormalizedItem> => {
    return useMemo(() => {
      const hiddenColumns = [];

      if (type === ETypeSlice.GROUPS) hiddenColumns.push('alias');
      if (type === ETypeSlice.HOSTS)
        hiddenColumns.push('hostCount', 'averageProceeds');

      return {
        ...state,
        hiddenColumns,
      };
    }, [state, type]);
  };

  return (
    <Table
      data={data}
      columns={COLUMNS}
      isLoading={loading}
      useControlledState={useControlledState}
      emptyDataPlaceholder={t(
        apiCalled ? 'report.placeholder' : 'report.placeholder.emptyDates'
      )}
      className={s.table}
    />
  );
};
